import { css, cx } from '@emotion/css'
import type { CSSObject } from '@emotion/react'
import { useMantineTheme } from '@mantine/core'
import type { ButtonProps } from 'components/Button/Button'
import { Button } from 'components/Button/Button'
import React, { useEffect, useState } from 'react'
import { colors } from 'settings/colors'
import { useIsWideScreen } from 'utils/useIsWideScreen'

export type ToggleButtonProps<T> = {
  buttonProps?: Omit<ButtonProps, 'state' | 'stateChildren' | 'variant'>
  data: Array<{
    label: React.ReactNode
    value: T
  }>
  onChange: (value: T) => void
  defaultValue?: T
  value?: T
}

export const ToggleButton = <T,>({
  buttonProps,
  data,
  onChange,
  defaultValue,
  value,
}: ToggleButtonProps<T>) => {
  const theme = useMantineTheme()
  const { className, ...restOfButtonProps } = buttonProps ?? {}
  const [selectedButton, setSelectedButton] = useState<T>(value ?? defaultValue ?? data[0]?.value)

  const toggleButtonWrapperStyles: CSSObject = {
    display: 'flex',
    flexWrap: 'nowrap',
    border: `1px solid ${colors.grey800}`,
    borderRadius: '3px',
    padding: '3px',
    width: 'fit-content',
    backgroundColor: theme.colors.white[0],
  }

  const handleToggle = (value: T) => {
    setSelectedButton(value)

    onChange(value)
  }

  useEffect(() => {
    if (value !== selectedButton) setSelectedButton(value as T)
  }, [value])

  const isWideScreen = useIsWideScreen()

  return (
    <div css={toggleButtonWrapperStyles}>
      {data.map(({ label, value }, i) => {
        const backgroundColor = value === selectedButton ? colors.grey800 : 'transparent'
        const color = value === selectedButton ? theme.colors.white[0] : theme.colors.midnight[0]
        const borderColor = value === selectedButton ? colors.grey800 : 'transparent'

        return (
          <Button
            variant="tertiary"
            className={cx(
              className,
              css({
                backgroundColor: `${backgroundColor} !important`,
                color: `${color} !important`,
                borderColor: `${borderColor} !important`,
              }),
            )}
            key={i}
            onClick={() => handleToggle(value)}
            {...restOfButtonProps}
            size={isWideScreen ? 'sm' : 'lg'}
          >
            {label}
          </Button>
        )
      })}
    </div>
  )
}
